import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, Search, User, Clock, Activity } from 'lucide-react';

const ACTION_COLORS = {
  login: 'bg-blue-100 text-blue-700',
  register: 'bg-emerald-100 text-emerald-700',
  create_triage: 'bg-sky-100 text-sky-700',
  complete_triage: 'bg-emerald-100 text-emerald-700',
  escalate_triage: 'bg-purple-100 text-purple-700',
  create_patient: 'bg-teal-100 text-teal-700',
  update_settings: 'bg-amber-100 text-amber-700',
  update_ai_config: 'bg-indigo-100 text-indigo-700',
  create_tenant: 'bg-sky-100 text-sky-700',
  create_plan: 'bg-orange-100 text-orange-700',
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/audit/logs?limit=100${search ? `&action=${search}` : ''}`)
      .then(res => {
        setLogs(res.data.logs || []);
        setTotal(res.data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search]);

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px]">
      <div>
        <h1 data-testid="audit-title" className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Audit Logs</h1>
        <p className="text-slate-500 mt-1">{total} log entries - HIPAA compliance tracking</p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="audit-search"
            placeholder="Filter by action..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-700">Timestamp</TableHead>
                <TableHead className="font-semibold text-slate-700">User</TableHead>
                <TableHead className="font-semibold text-slate-700">Action</TableHead>
                <TableHead className="font-semibold text-slate-700">Resource</TableHead>
                <TableHead className="font-semibold text-slate-700">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                    <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No audit logs found</p>
                  </TableCell>
                </TableRow>
              ) : logs.map((log) => (
                <TableRow key={log.id} data-testid={`audit-row-${log.id}`} className="hover:bg-slate-50">
                  <TableCell className="text-slate-600">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-sm">{new Date(log.timestamp).toLocaleString()}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-slate-500" />
                      </div>
                      <span className="text-sm font-medium text-slate-900">{log.user_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700'} text-xs font-medium`}>
                      {log.action?.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell><span className="text-sm text-slate-600">{log.resource}</span></TableCell>
                  <TableCell><span className="text-sm text-slate-500 max-w-[300px] truncate block">{log.details || '-'}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
